/**
 * MassTriagePool — pool de pacientes ficticios para Mass Triage Mode.
 *
 * Cada paciente viene con: datos demográficos, queja principal, 4-5 vitales
 * iniciales, y el triage correcto (ground truth) para el scoring.
 *
 * Estos casos se generan en v1 localmente desde un pool curado para dar
 * variedad sin depender de la IA en el MVP. Una vez validado el flujo,
 * Sprint 3 reemplaza este pool con generación procedural via Kimi.
 */

import type { TriagePriority } from '../types/medical';

export interface MassTriagePatientTemplate {
  id: string;
  name: string;
  age: number;
  sex: 'M' | 'F';
  chiefComplaint: string;
  icon: string;
  /** Vitales iniciales del paciente. */
  vitals: {
    hr: number;
    sbp: number;
    dbp: number;
    spo2: number;
    rr: number;
    temp: number;
    pain?: number; // 0-10
    glasgow?: number;
  };
  /** Triage correcto esperado (ground truth para scoring). */
  expected: TriagePriority;
  /** Explicación del triage para mostrar al alumno en el debrief. */
  rationale: string;
  /** Si este paciente se deteriora si no se prioriza a tiempo: cuánto empeoran sus vitales por minuto. */
  deteriorationPerMin?: {
    hr?: number;
    sbp?: number;
    spo2?: number;
    rr?: number;
  };
}

export const MASS_TRIAGE_POOL: MassTriagePatientTemplate[] = [
  {
    id: 'mt-stemi',
    name: 'Raúl M.',
    age: 62,
    sex: 'M',
    chiefComplaint: 'Dolor torácico opresivo 20 min, irradia a brazo izq',
    icon: '🫀',
    vitals: { hr: 115, sbp: 95, dbp: 60, spo2: 93, rr: 22, temp: 36.7, pain: 9 },
    expected: 'immediate',
    rationale: 'Dolor torácico típico + hipotensión + taquicardia + SpO₂ al límite → sospecha IAM con compromiso hemodinámico.',
    deteriorationPerMin: { sbp: -3, hr: 4, spo2: -0.5 },
  },
  {
    id: 'mt-shock',
    name: 'Lorena V.',
    age: 45,
    sex: 'F',
    chiefComplaint: 'Fiebre 3 días, confusión hace 2 horas',
    icon: '🦠',
    vitals: { hr: 130, sbp: 82, dbp: 50, spo2: 91, rr: 28, temp: 39.4, pain: 4, glasgow: 13 },
    expected: 'immediate',
    rationale: 'qSOFA ≥2 (taquipnea + hipotensión + AMS) + fiebre alta → shock séptico.',
    deteriorationPerMin: { sbp: -2, hr: 3, spo2: -0.4 },
  },
  {
    id: 'mt-asma',
    name: 'Felipe R.',
    age: 19,
    sex: 'M',
    chiefComplaint: 'Crisis asmática, uso músculos accesorios',
    icon: '🫁',
    vitals: { hr: 124, sbp: 135, dbp: 85, spo2: 88, rr: 32, temp: 36.5, pain: 3 },
    expected: 'immediate',
    rationale: 'SpO₂ < 90% + taquipnea severa + uso accesorios → crisis severa, requiere broncodilatadores urgentes.',
    deteriorationPerMin: { spo2: -0.8, rr: 1 },
  },
  {
    id: 'mt-fractura',
    name: 'Patricia L.',
    age: 71,
    sex: 'F',
    chiefComplaint: 'Caída, dolor en cadera derecha, no puede caminar',
    icon: '🦴',
    vitals: { hr: 92, sbp: 140, dbp: 85, spo2: 97, rr: 18, temp: 36.8, pain: 8 },
    expected: 'delayed',
    rationale: 'Probable fractura de cadera — dolor severo pero hemodinámica estable. Necesita Rx y analgesia en <30 min.',
  },
  {
    id: 'mt-migraine',
    name: 'Camila P.',
    age: 28,
    sex: 'F',
    chiefComplaint: 'Cefalea pulsátil, fotofobia, náuseas. Migrañas conocidas',
    icon: '🧠',
    vitals: { hr: 82, sbp: 118, dbp: 72, spo2: 98, rr: 15, temp: 36.6, pain: 7 },
    expected: 'delayed',
    rationale: 'Cefalea típica de migraña en paciente con antecedentes. Analgesia y antiemético. Bandera roja solo si cambia el patrón.',
  },
  {
    id: 'mt-resfrio',
    name: 'Tomás G.',
    age: 34,
    sex: 'M',
    chiefComplaint: 'Congestión nasal, tos seca, sin fiebre',
    icon: '🤧',
    vitals: { hr: 78, sbp: 122, dbp: 78, spo2: 98, rr: 14, temp: 37.1, pain: 2 },
    expected: 'minor',
    rationale: 'IRA alta sin signos de alarma — manejo ambulatorio.',
  },
  {
    id: 'mt-esguince',
    name: 'Sebastián T.',
    age: 22,
    sex: 'M',
    chiefComplaint: 'Esguince tobillo jugando fútbol, leve edema',
    icon: '🦶',
    vitals: { hr: 84, sbp: 120, dbp: 74, spo2: 99, rr: 16, temp: 36.5, pain: 5 },
    expected: 'minor',
    rationale: 'Trauma menor sin compromiso vascular ni sistémico. Puede esperar, radiografía ambulatoria.',
  },
  {
    id: 'mt-acv',
    name: 'Oscar N.',
    age: 74,
    sex: 'M',
    chiefComplaint: 'Hemiparesia derecha súbita hace 45 min, afasia',
    icon: '🧠',
    vitals: { hr: 98, sbp: 178, dbp: 102, spo2: 96, rr: 18, temp: 36.7, glasgow: 14 },
    expected: 'immediate',
    rationale: 'Ventana terapéutica para ACV isquémico. Cada minuto pierde ~2M neuronas.',
    deteriorationPerMin: { sbp: 0.5 },
  },
  {
    id: 'mt-cad',
    name: 'María E.',
    age: 38,
    sex: 'F',
    chiefComplaint: 'Aliento frutal, polidipsia, vómitos. DM1 conocida.',
    icon: '💉',
    vitals: { hr: 118, sbp: 100, dbp: 62, spo2: 96, rr: 28, temp: 37.0, pain: 4 },
    expected: 'immediate',
    rationale: 'Respiración Kussmaul + taquicardia + DM1 + vómitos → cetoacidosis diabética. Requiere insulina y fluidos YA.',
    deteriorationPerMin: { hr: 2, sbp: -1 },
  },
  {
    id: 'mt-ansioso',
    name: 'Daniela K.',
    age: 26,
    sex: 'F',
    chiefComplaint: 'Ataque de pánico, hiperventilación, parestesias',
    icon: '😰',
    vitals: { hr: 108, sbp: 130, dbp: 85, spo2: 99, rr: 24, temp: 36.6, pain: 3 },
    expected: 'delayed',
    rationale: 'Cuadro de ansiedad con hiperventilación — descartar primero orgánico (ECG, glicemia), pero no es riesgo vital.',
  },
  {
    id: 'mt-intoxicacion',
    name: 'Diego A.',
    age: 42,
    sex: 'M',
    chiefComplaint: 'Intoxicación alcohólica + trauma facial',
    icon: '🍷',
    vitals: { hr: 102, sbp: 110, dbp: 70, spo2: 95, rr: 16, temp: 36.4, glasgow: 12 },
    expected: 'delayed',
    rationale: 'Glasgow 12 en contexto alcohólico — necesita monitorización y descartar TEC, pero vitales estables.',
  },
  {
    id: 'mt-trauma-grave',
    name: 'N.N.',
    age: 35,
    sex: 'M',
    chiefComplaint: 'Accidente de tránsito, múltiples lesiones, inconsciente',
    icon: '🚑',
    vitals: { hr: 140, sbp: 70, dbp: 40, spo2: 85, rr: 34, temp: 35.8, glasgow: 8 },
    expected: 'immediate',
    rationale: 'Shock hipovolémico + Glasgow 8 + múltiples lesiones → politraumatismo grave. ABC comprometido.',
    deteriorationPerMin: { sbp: -4, hr: 5, spo2: -1, rr: 1 },
  },
];

/**
 * Devuelve un subset aleatorio balanceado para una sesión de Mass Triage.
 * Trata de incluir 2-3 inmediatos, 1-2 ámbar, 1-2 verdes para variedad.
 */
export function samplePool(n = 6): MassTriagePatientTemplate[] {
  const byPriority: Record<TriagePriority, MassTriagePatientTemplate[]> = {
    immediate: MASS_TRIAGE_POOL.filter(p => p.expected === 'immediate'),
    delayed: MASS_TRIAGE_POOL.filter(p => p.expected === 'delayed'),
    minor: MASS_TRIAGE_POOL.filter(p => p.expected === 'minor'),
    expectant: MASS_TRIAGE_POOL.filter(p => p.expected === 'expectant'),
    deceased: MASS_TRIAGE_POOL.filter(p => p.expected === 'deceased'),
  };
  const pick = (arr: MassTriagePatientTemplate[], k: number): MassTriagePatientTemplate[] => {
    const copy = [...arr];
    const out: MassTriagePatientTemplate[] = [];
    for (let i = 0; i < k && copy.length > 0; i++) {
      const idx = Math.floor(Math.random() * copy.length);
      out.push(copy.splice(idx, 1)[0]);
    }
    return out;
  };
  // Distribución: ~40% inmediato, ~35% delayed, ~25% minor. Ajustable.
  const nImm = Math.max(1, Math.round(n * 0.4));
  const nDel = Math.max(1, Math.round(n * 0.35));
  const nMin = Math.max(1, n - nImm - nDel);
  const picked = [
    ...pick(byPriority.immediate, nImm),
    ...pick(byPriority.delayed, nDel),
    ...pick(byPriority.minor, nMin),
  ].slice(0, n);
  // Shuffle final para que no aparezcan ordenados por prioridad
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [picked[i], picked[j]] = [picked[j], picked[i]];
  }
  return picked;
}
